// CREATIVE_BETA_INTERNAL_ACCESS — the Cortex half of one canonical contract.
//
// The backend authority is services/creativeBetaAccessPolicy.js. This file
// exists so Cortex's *presentation* (which tiles navigate, which routes the
// swipe sequence offers, what Ask EDEN will act on) is derived from the same
// decision the server enforces, instead of being a second opinion that drifts.
//
// Incident this closes: a Cortex client session reached Creative Trafficking,
// Creative Execution and Creative Analysis with a two-finger trackpad swipe.
// The hub tiles were disabled, but the routes were registered for `client`,
// sat in the swipe sequence, and the APIs behind them granted `client` too.
//
// Contract — identical to the backend:
//   - superadmin (Quantum) : permitted.
//   - creative             : denied.
//   - client               : denied.
//   - anything else        : denied (fail closed).
//
// Cortex issues no superadmin session, so in practice every Cortex principal
// is denied. The role is still evaluated rather than hardcoded to `false` so
// this file states the same rule as the server rather than a coincidence.
//
// This is presentation governance, NOT the security boundary. The boundary is
// the backend policy: a client that edits localStorage, flips a flag or types
// the URL still gets a 403 from every API behind these pages.

// Canonical protected surfaces. `id` matches the backend PROTECTED_ROUTE_IDS.
export const PROTECTED_BETA_SURFACES = Object.freeze([
  Object.freeze({
    id: "creative-trafficking",
    path: "/creative-trafficking",
    label: "Creative Operations",
  }),
  Object.freeze({
    id: "creative-executions",
    path: "/creative-executions",
    label: "Execution",
  }),
  Object.freeze({
    id: "creative-analysis",
    path: "/creative-analysis",
    label: "Creative Insights",
  }),
  Object.freeze({
    id: "creative-optimus",
    path: "/creative-optimus",
    label: "Creative Optimus",
  }),
]);

export const PROTECTED_BETA_ROUTES = Object.freeze(
  PROTECTED_BETA_SURFACES.map((surface) => surface.path)
);

// Client-safe copy. Says nothing about internal capabilities, Quantum, roles
// or why the surface exists.
export const BETA_SURFACE_UNAVAILABLE_COPY =
  "This area isn't available for your account yet.";

// Creative Operations and Execution are not unbuilt: Cphere runs them on the
// client's behalf. Saying "coming soon" there was untrue. Same rule as above —
// nothing about internal capabilities, portals or roles — and the tile still
// navigates nowhere.
export const BETA_SURFACE_OPERATED_BY_CPHERE_COPY =
  "Operated by Cphere for your campaigns. Setup status appears on your campaign strategy page.";

// Where a denied navigation lands. Creative Studio is the nearest permitted
// Cortex surface for the creative hub; everything else falls back to the
// dashboard.
export const BETA_DENIAL_LANDING_PATH = "/nexus-hub";

function normalizeRole(role) {
  return String(role ?? "")
    .toLowerCase()
    .replace(/[_\s]/g, "")
    .trim();
}

/**
 * Normalizes a location into a comparable pathname. Query strings and hashes
 * are stripped so `/creative-analysis?beta=true#x` cannot masquerade as an
 * unprotected route.
 */
export function normalizeRoutePath(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const withoutHash = raw.split("#")[0];
  const withoutQuery = withoutHash.split("?")[0];
  const collapsed = withoutQuery.replace(/\/+$/, "");
  return collapsed || "/";
}

/** True when a path (with any query/hash/trailing slash) is a protected Beta surface. */
export function isProtectedBetaRoute(value) {
  const normalized = normalizeRoutePath(value).toLowerCase();
  if (!normalized) return false;
  return PROTECTED_BETA_ROUTES.some(
    (route) => normalized === route || normalized.startsWith(`${route}/`)
  );
}

/**
 * The decision. Mirrors resolveCreativeBetaAccess on the server.
 * Reads only the session role — never a feature flag, query param or
 * browser-storage value.
 */
export function resolveBetaSurfaceAccess(role) {
  return normalizeRole(role) === "superadmin";
}

/** Strips every protected Beta surface from a navigation sequence. */
export function withoutProtectedBetaRoutes(routes) {
  if (!Array.isArray(routes)) return [];
  return routes.filter((route) => !isProtectedBetaRoute(route));
}
